import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { CheckCircle2, XCircle, TrendingUp, ArrowLeft, Award } from 'lucide-react';

const Results = ({ progress }) => {
  const navigate = useNavigate();
  const result = progress?.lastTestResult;

  if (!result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-gray-600 mb-4">No test results available</p>
            <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { score, total, percentage, passed, time_taken, incorrect_answers } = result;
  const minutes = Math.floor(time_taken / 60);
  const seconds = time_taken % 60;

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        <Card className="shadow-2xl" data-testid="results-card">
          <CardHeader className={`${passed ? 'bg-gradient-to-r from-green-600 to-emerald-600' : 'bg-gradient-to-r from-red-600 to-pink-600'} text-white rounded-t-lg text-center`}>
            <div className="flex justify-center mb-4">
              {passed ? (
                <div className="bg-white/20 p-4 rounded-full">
                  <Award className="h-16 w-16" />
                </div>
              ) : (
                <div className="bg-white/20 p-4 rounded-full">
                  <TrendingUp className="h-16 w-16" />
                </div>
              )}
            </div>
            <CardTitle className="text-4xl font-bold">
              {passed ? 'Congratulations!' : 'Good Effort!'}
            </CardTitle>
            <CardDescription className="text-white/90 text-lg mt-2">
              {passed ? 'You passed the test!' : 'Keep practicing to improve'}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-8">
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <Card className="text-center border-2" data-testid="score-card">
                <CardContent className="p-6">
                  <div className="text-5xl font-bold text-purple-600 mb-2">{score}</div>
                  <p className="text-sm text-gray-600">Questions Correct</p>
                  <p className="text-xs text-gray-500 mt-1">out of {total}</p>
                </CardContent>
              </Card>
              <Card className={`text-center border-2 ${passed ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`} data-testid="percentage-card">
                <CardContent className="p-6">
                  <div className={`text-5xl font-bold ${passed ? 'text-green-600' : 'text-red-600'} mb-2`}>{percentage}%</div>
                  <p className="text-sm text-gray-600">Score</p>
                  <p className="text-xs text-gray-500 mt-1">{passed ? '70%+ to pass' : 'Need 70% to pass'}</p>
                </CardContent>
              </Card>
              <Card className="text-center border-2" data-testid="time-card">
                <CardContent className="p-6">
                  <div className="text-5xl font-bold text-indigo-600 mb-2">{minutes}:{seconds.toString().padStart(2, '0')}</div>
                  <p className="text-sm text-gray-600">Time Taken</p>
                  <p className="text-xs text-gray-500 mt-1">minutes</p>
                </CardContent>
              </Card>
            </div>

            {incorrect_answers && incorrect_answers.length > 0 && (
              <Card className="border-2 mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-500" />
                    Review Incorrect Answers ({incorrect_answers.length})
                  </CardTitle>
                  <CardDescription>Learn from your mistakes to improve</CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {incorrect_answers.map((item, index) => (
                      <AccordionItem key={index} value={`item-${index}`} data-testid={`incorrect-answer-${index}`}>
                        <AccordionTrigger className="text-left">
                          <div className="flex items-start gap-3 flex-1">
                            <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-semibold">Q</span>
                            <span className="flex-1">{item.question}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pl-10 space-y-3">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-gray-600 font-medium">Topic:</span>
                              <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">{item.topic}</span>
                            </div>
                            <div className="bg-red-50 border border-red-200 p-3 rounded">
                              <p className="text-sm font-medium text-red-700">Your Answer: Option {item.your_answer + 1}</p>
                            </div>
                            <div className="bg-green-50 border border-green-200 p-3 rounded">
                              <p className="text-sm font-medium text-green-700">Correct Answer: Option {item.correct_answer + 1}</p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                              <p className="text-sm font-medium text-blue-700 mb-1">Explanation:</p>
                              <p className="text-sm text-gray-700">{item.explanation}</p>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            )}

            {passed && (
              <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 mb-6">
                <CardContent className="p-6 flex items-center gap-4">
                  <CheckCircle2 className="h-12 w-12 text-green-600" />
                  <div>
                    <p className="font-bold text-green-900 text-lg">Excellent Work!</p>
                    <p className="text-sm text-green-700 mt-1">
                      You've successfully passed this test. Keep up the great work!
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex gap-4">
              <Button
                onClick={() => navigate(-1)}
                className="flex-1 h-12 text-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                data-testid="back-btn"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Results;
